import { inject, Injectable, OnDestroy } from '@angular/core';
import { AnomalyApiService } from '@buda/app/services/api/anomaly-api.service';
import {
  Anomaly,
  AnomalyDetailsProperties,
  AnomalyFeedback,
  AnomalyFeedbackType,
  AnomalyFilter,
  AnomalySeverity,
} from '@buda/app/types/anomaly.types';
import { Memoize } from '@common-services/util/cache.util';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

@Injectable()
export class AnomalyUtilService implements OnDestroy {
  private anomalyApiService = inject(AnomalyApiService);

  filter: BehaviorSubject<AnomalyFilter> = new BehaviorSubject(null);

  filterVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly IMAGE_PREFIX: string = 'data:image/png;base64,';

  private readonly DATE_FORMAT: string = 'DD.MM.YYYY HH:mm';

  private businessStructures$: Observable<string[]>;

  private feedbackTypes$: Observable<AnomalyFeedbackType[]>;

  private feedbackTypesForFilter$: Observable<AnomalyFeedbackType[]>;

  private severityTypes$: Observable<AnomalySeverity[]>;

  private businessUnit: string;

  getBusinessStructures(businessUnit: string): Observable<string[]> {
    if (!this.businessStructures$ || businessUnit !== this.businessUnit) {
      this.businessStructures$ = this.anomalyApiService.fetchBusinessStructures(businessUnit).pipe(shareReplay());
      this.businessUnit = businessUnit;
    }
    return this.businessStructures$;
  }

  getFeedbackTypes(): Observable<AnomalyFeedbackType[]> {
    if (!this.feedbackTypes$) {
      this.feedbackTypes$ = this.anomalyApiService.fetchFeedBackTypes(false).pipe(shareReplay());
    }
    return this.feedbackTypes$;
  }

  getSeverityTypes(): Observable<AnomalySeverity[]> {
    if (!this.severityTypes$) {
      this.severityTypes$ = this.anomalyApiService.fetchSeverityTypes().pipe(shareReplay());
    }
    return this.severityTypes$;
  }

  toggleFilter(): void {
    const isVisible: boolean = this.filterVisible.getValue();
    this.filterVisible.next(!isVisible);
  }

  getFormattedDate(dateIsoString: string): string {
    if (dateIsoString) {
      return moment(dateIsoString).format(this.DATE_FORMAT);
    }
    return '-';
  }

  @Memoize()
  getFormattedAnomalyDetailsData(anomaly: Anomaly, anomalyDetailsProperties: AnomalyDetailsProperties): string {
    if (!anomaly[anomalyDetailsProperties]) {
      return '-';
    }
    const anomalyDetails: string | AnomalyFeedback | number = anomaly[anomalyDetailsProperties];
    switch (anomalyDetailsProperties) {
      default:
        return anomalyDetails as string;
      case AnomalyDetailsProperties.feedback:
        // return this.getFeedBackTypeDisplayName((anomalyDetails as AnomalyFeedback).feedbackType);
        return (anomalyDetails as AnomalyFeedback).feedbackType;
      case AnomalyDetailsProperties.duration:
        return `${Math.ceil((anomalyDetails as number) / 60)} min`;
      case AnomalyDetailsProperties.alertTime:
        return this.getFormattedDate(anomalyDetails as string);
      case AnomalyDetailsProperties.endTime:
        return this.getFormattedDate(anomalyDetails as string);
    }
  }

  getFeedBackTypeDisplayName(anomalyFeedbackType: AnomalyFeedbackType): string {
    let words: string[] = anomalyFeedbackType.split('_');
    words = words.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase());
    return words.join(' ');
  }

  ngOnDestroy(): void {}
}
